/**
 * LES INTERRUPTEURS PRODUIT, LUS AU RUNTIME (DEV-1508 lot 3).
 * ============================================================================
 *
 * INTENTION : pouvoir ouvrir une page à une société, regarder, puis refermer,
 * en une commande et sans reconstruire le bundle. Le dyno porte la population
 * armée, le navigateur la lit UNE fois après l'authentification, et chaque écran
 * demande simplement « suis-je dedans ? ».
 *
 * Deux règles tenues ici :
 *  • une seule lecture par session. `charger()` est idempotent : dix écrans qui
 *    l'appellent ne font qu'un appel, et un appel en vol est partagé ;
 *  • fermé par défaut. Tant que la route n'a pas répondu, et pour toujours si
 *    elle répond mal ou pas du tout, tout interrupteur vaut « personne ». Un
 *    réseau qui tousse ne doit pas ouvrir une page à tout le monde.
 *
 * Et une troisième, depuis que le serveur ne sert plus un courriel qu'en
 * empreinte `sha256:<hex>` : l'empreinte du courriel de la session se calcule
 * (Web Crypto, asynchrone) DANS `charger()`, avant que la promesse ne se
 * résolve. Les gardes attendent `charger()` : quand elles tranchent, l'état est
 * déjà connu, et la page ne s'affiche pas fermée pour s'ouvrir un instant plus
 * tard. Une empreinte calculée pour une autre session (déconnexion puis
 * connexion sous un autre compte) n'arme jamais la nouvelle : elle porte le
 * courriel pour lequel elle a été faite, et `charger()`, rappelé à chaque
 * navigation, la refait.
 */
import { defineStore } from "pinia"
import { computed, ref, shallowRef } from "vue"
import { useAuthStore } from "@core/stores/auth.store"
import { sessionDeLEquipeSelonLeJeton } from "@core/stores/auth.staff"
import {
    ROUTE_INTERRUPTEURS,
    empreinteDuCourriel,
    estArme,
    lireValeurServie,
    normaliserCourriel,
    porteUneEmpreinte,
    type ValeurInterrupteur,
} from "@modules/data/config/enedis-page-donnees-v2.flag"

/**
 * La session connectée fait-elle partie de la population ? Lit la société, le
 * courriel de connexion et la mention d'équipe au moment de l'appel.
 *
 * La mention d'équipe se lit dans le jeton de session signé par le serveur
 * (DEV-2306), jamais dans `adminHomeys` : ce drapeau vit aussi dans le stockage
 * du navigateur, où un client peut l'écrire. Il ne sert qu'au suivi d'usage.
 */
/** L'empreinte d'un courriel, et le courriel (normalisé) pour lequel elle vaut. */
interface EmpreinteDeSession {
    courriel: string
    empreinte: string | null
}

function armePourLaSession(
    valeur: ValeurInterrupteur,
    empreinte: EmpreinteDeSession | null
): boolean {
    const auth = useAuthStore()
    const courriel = normaliserCourriel(auth.username)
    return estArme(valeur, {
        companyId: auth.companyId,
        username: auth.username,
        staff: sessionDeLEquipeSelonLeJeton(auth.token),
        // Seulement si elle a été calculée pour CE courriel-là.
        empreinteCourriel:
            courriel !== null && empreinte?.courriel === courriel
                ? empreinte.empreinte
                : null,
    })
}

export const useInterrupteursStore = defineStore("interrupteurs", () => {
    /** Ce que le dyno dit de la page « Données du compteur ». */
    const enedisPageDonneesV2 = ref<ValeurInterrupteur>("off")

    /**
     * Ce que le dyno dit de la page « Optimisation du Turpe (V2) » (DEV-1892) :
     * son entrée de menu et son adresse. Même défaut fermé que la page données.
     */
    const optimturpeV2 = ref<ValeurInterrupteur>("off")

    /** Vrai une fois la route lue, qu'elle ait répondu ou échoué. */
    const lu = ref(false)

    /** L'empreinte du courriel de la session, et le courriel qu'elle couvre. */
    const empreinteSession = shallowRef<EmpreinteDeSession | null>(null)

    /** La lecture en vol, partagée par tous les appelants. */
    let enVol: Promise<void> | null = null

    /**
     * Calcule l'empreinte du courriel de la session courante, quand une liste
     * en porte une et qu'elle n'est pas déjà connue pour ce courriel. Ne
     * rejette jamais : un échec la laisse à `null`, ce qui ferme les jetons
     * `sha256:` et rien d'autre.
     */
    async function assurerEmpreinte(): Promise<void> {
        if (
            !porteUneEmpreinte(enedisPageDonneesV2.value) &&
            !porteUneEmpreinte(optimturpeV2.value)
        ) {
            return
        }
        const courriel = normaliserCourriel(useAuthStore().username)
        if (courriel === null) return
        if (empreinteSession.value?.courriel === courriel) return
        const empreinte = await empreinteDuCourriel(courriel)
        // La session a pu changer pendant le calcul : une empreinte périmée
        // n'écrase pas celle de la session suivante (elle n'armerait de toute
        // façon personne, mais elle obligerait à la recalculer).
        if (normaliserCourriel(useAuthStore().username) !== courriel) return
        empreinteSession.value = { courriel, empreinte }
    }

    /**
     * Lit la route une seule fois par session, puis s'assure que l'empreinte
     * du courriel de la session courante est connue.
     *
     * Ne rejette jamais : une erreur laisse les interrupteurs désarmés, ce qui
     * est exactement l'état d'avant.
     */
    async function charger(): Promise<void> {
        if (lu.value) return assurerEmpreinte()
        if (enVol) return enVol
        enVol = (async () => {
            try {
                const reponse = await fetch(ROUTE_INTERRUPTEURS, {
                    headers: { Accept: "application/json" },
                })
                if (!reponse.ok) return
                const corps = (await reponse.json()) as Record<string, unknown>
                enedisPageDonneesV2.value = lireValeurServie(
                    corps?.enedis_page_donnees_v2
                )
                optimturpeV2.value = lireValeurServie(corps?.optimturpe_v2)
                // AVANT de dire « lu » : une garde qui attend `charger()` ne
                // doit jamais trancher sur une empreinte pas encore calculée.
                await assurerEmpreinte()
            } catch {
                // Route absente, hors ligne, corps illisible : on reste fermé.
            } finally {
                lu.value = true
                enVol = null
            }
        })()
        return enVol
    }

    /** Remet le store à son état de départ. Réservé aux tests. */
    function reinitialiser(): void {
        enedisPageDonneesV2.value = "off"
        optimturpeV2.value = "off"
        lu.value = false
        empreinteSession.value = null
        enVol = null
    }

    /**
     * Vrai quand la session connectée fait partie de la population armée pour
     * la page « Données du compteur ».
     *
     * Le calcul lit la société, le courriel de connexion et le drapeau d'équipe
     * À CHAQUE ÉVALUATION : une session qui s'authentifie après la lecture de la
     * route s'arme sans qu'on relise quoi que ce soit.
     */
    const enedisPageDonneesV2Arme = computed(() =>
        armePourLaSession(enedisPageDonneesV2.value, empreinteSession.value)
    )

    /**
     * Vrai quand la session connectée fait partie de la population armée pour
     * la page « Optimisation du Turpe (V2) » (DEV-1892). Même lecture des
     * jetons, même calcul à chaque évaluation.
     */
    const optimturpeV2Arme = computed(() =>
        armePourLaSession(optimturpeV2.value, empreinteSession.value)
    )

    return {
        enedisPageDonneesV2,
        enedisPageDonneesV2Arme,
        optimturpeV2,
        optimturpeV2Arme,
        lu,
        charger,
        reinitialiser,
    }
})
